

// export default App;
import React, { useState, useEffect } from 'react';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import WidthInspectionDashboard from './components/InspectionDashboard';
import WidthHistoryView from './components/HistoryView';
import ReportsView from './components/ReportsView';
import DefectView from './components/DefectView';
import ChatbotVeiw from './components/Chatbot';
import SettingsPage from './components/Settings';
import { trackPageView, trackEvent, trackError, initializeAnalytics } from './utils/analytics';
import CostSavingsView from './components/CostSavingsView';

// Analytics Configuration
const GA4_MEASUREMENT_ID = 'G-B5NZJGWFEC';

// Cognito Configuration
const poolData = {
  UserPoolId: 'us-east-1_2Ys8CjpHH',
  ClientId: '2dsf1amtk7872jhgj985f4qhkb'
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedInitialDate, setSelectedInitialDate] = useState(null);
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = () => {
    try {
      const userPool = new CognitoUserPool(poolData);
      const currentUser = userPool.getCurrentUser();

      if (!currentUser) {
        setIsLoading(false);
        setIsAuthenticated(false);
        return;
      }

      currentUser.getSession((err, session) => {
        if (err || !session?.isValid()) {
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
          return;
        }

        currentUser.getUserData((err, userData) => {
          if (!err && userData) {
            setUser({
              username: userData.Username || currentUser.getUsername()
            });
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            setUser(null);
          }
          setIsLoading(false);
        });
      });
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    }
  };

  const handleLogin = (username) => {
    setIsAuthenticated(true);
    setUser({ username });
    if (analyticsInitialized) {
      trackEvent('login_success', {
        timestamp: new Date().toISOString(),
        username
      });
    }
  };

  const handleLogout = () => {
    try {
      const userPool = new CognitoUserPool(poolData);
      const currentUser = userPool.getCurrentUser();
      
      if (currentUser) {
        currentUser.signOut();
      }
      
      // Clear all auth states
      setUser(null);
      setIsAuthenticated(false);
      setCurrentPage('dashboard');
      
      if (analyticsInitialized) {
        trackEvent('user_logout', {
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handlePageChange = (page, date = null) => {
    setCurrentPage(page);
    if (date && analyticsInitialized) {
      setSelectedInitialDate(date);
    } else {
      setSelectedInitialDate(null);
    }
  };

  const trackComponentView = (componentName, additionalData = {}) => {
    if (analyticsInitialized) {
      trackEvent('component_view', {
        component_name: componentName,
        ...additionalData,
        timestamp: new Date().toISOString()
      });
    }
  };

// In the renderPage() function, add this case:
const renderPage = () => {
  switch(currentPage) {
    case 'DefectViewer':
      trackComponentView('DefectView');
      return <DefectView/>;
      
    // case 'settings':
    //   trackComponentView('SettingsView');
    //   return <SettingsPage />;
      
    case 'historie':
      trackComponentView('HistoryView', { initial_date: selectedInitialDate });
      return <WidthHistoryView initialDate={selectedInitialDate} />;
      
    case 'reports':
      trackComponentView('ReportsView');
      return <ReportsView />;


      
    default:
      trackComponentView('InspectionDashboard');
      return <WidthInspectionDashboard/>;
  }
};

  const handleError = (error) => {
    if (analyticsInitialized) {
      trackError(error, {
        page: currentPage,
        timestamp: new Date().toISOString()
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1216]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginPage
        onLoginSuccess={handleLogin}
        poolData={poolData}
      />
    );
  }

  return (
    <Layout 
      activePage={currentPage} 
      onPageChange={handlePageChange}
      onError={handleError}
      onLogout={handleLogout}
      user={user}
    >
      {renderPage()}
    </Layout>
  );
}


export default App;